import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import { SparklesIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import DreamForm from './forms/DreamForm';
import { Button, Alert, Spinner, Badge } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface Dream {
  id: number;
  title: string;
  description?: string;
  inspiration?: string;
  driver?: string;
  is_active?: boolean;
  related_goals?: { id: number; title: string }[];
  category?: { id: number; name: string } | null;
}

const DreamList: React.FC = () => {
  const [dreams, setDreams] = useState<Dream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDream, setSelectedDream] = useState<Dream | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchDreams = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/dreams/');
      setDreams(response.data);
    } catch (error) {
      console.error('Error fetching dreams:', error);
      setError('Failed to load dreams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDreams();
  }, []);

  const handleEdit = (dream: Dream) => {
    setSelectedDream(dream);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (dreamId: number) => {
    if (!window.confirm('Are you sure you want to delete this dream?')) return;

    try {
      const response = await apiClient.delete(`/api/dreams/${dreamId}/`);

      if (response.status >= 200 && response.status < 300) {
        setDreams(dreams.filter(d => d.id !== dreamId));
      } else {
        throw new Error(`Failed to delete dream (${response.status})`);
      }
    } catch (err) {
      console.error('Error deleting dream:', err);
      setDeleteError('Failed to delete dream. Please try again.');
      setTimeout(() => setDeleteError(null), 3000);
    }
  };

  const handleDreamUpdated = (updatedDream: Dream) => {
    setDreams(dreams.map(d => d.id === updatedDream.id ? updatedDream : d));
    setIsEditModalOpen(false);
    setSelectedDream(null);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24],
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      {deleteError && (
        <Alert variant="error">{deleteError}</Alert>
      )}

      {dreams.length === 0 && !error && !loading && (
        <p style={{
          textAlign: 'center',
          color: COLORS.text.secondary,
          paddingTop: SPACING[16],
          paddingBottom: SPACING[16],
          fontSize: TYPOGRAPHY.body.sizes.default,
        }}>No dreams found.</p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16],
      }}>
        {dreams.map((dream) => (
          <Link
            to={`/dreams/${dream.id}`}
            key={dream.id}
            style={{
              display: 'block',
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[16],
              textDecoration: 'none',
              color: 'inherit',
              transition: 'box-shadow 150ms ease',
            }}
            tabIndex={0}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING[12],
              }}>
                <div style={{
                  padding: SPACING[8],
                  backgroundColor: `${COLORS.accent.default}10`,
                  borderRadius: '50%',
                }}>
                  <SparklesIcon style={{
                    width: '20px',
                    height: '20px',
                    color: COLORS.accent.default,
                  }} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.size.base,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    color: COLORS.text.primary,
                    margin: 0,
                  }}>{dream.title}</h3>
                  <div style={{ display: 'flex', gap: SPACING[4], marginTop: SPACING[4] }}>
                    <Badge
                      variant={dream.is_active ? 'success' : 'default'}
                      size="sm"
                    >
                      {dream.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {dream.related_goals && dream.related_goals.length > 0 && (
                      <Badge variant="info" size="sm">
                        {dream.related_goals.length} goal{dream.related_goals.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: SPACING[8] }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => { e.preventDefault(); handleEdit(dream); }}
                  title="Edit dream"
                  style={{
                    padding: SPACING[4],
                    height: 'auto',
                    color: COLORS.text.tertiary,
                  }}
                >
                  <PencilIcon style={{ width: '16px', height: '16px' }} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => { e.preventDefault(); handleDelete(dream.id); }}
                  title="Delete dream"
                  style={{
                    padding: SPACING[4],
                    height: 'auto',
                    color: COLORS.text.tertiary,
                  }}
                >
                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                </Button>
              </div>
            </div>

            {dream.description && (
              <p style={{
                marginTop: SPACING[8],
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: COLORS.text.secondary,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}>{dream.description}</p>
            )}
          </Link>
        ))}
      </div>

      {selectedDream && (
        <DreamForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedDream(null);
          }}
          onDreamCreated={handleDreamUpdated}
          initialDream={selectedDream}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default DreamList;
